import { persistState, loadPersistedState, clearPersistedState } from './utils/state-persistence';

export type GameStatus = 'WAITING' | 'ACTIVE' | 'FINISHED';
export type PlayerRole = 'PLAYER' | 'SPECTATOR';

export interface Player {
  id: string;
  name: string;
  role: PlayerRole;
  isCaptain: boolean;
}

export interface EncryptedHint {
  encryptedText: string;
  delaySec: number;
  orderIndex: number;
  type?: string; // "text" | "auto_navigate"
}

export interface Location {
  id: string;
  order: number;
  encryptedRiddle: string;
  encryptedHint: string; // legacy single hint (backward compat)
  hintDelaySec: number;
  timeLimitSec: number;
  answerType: 'ADDRESS' | 'COORDINATES';
  geofenceCenter?: { lat: number; lon: number };
  geofenceRadiusM?: number;
  siteCodeHash: string;
  encryptedAnswerData?: string; // offline mode: encrypted geo answer data
  answerHash?: string;          // offline mode: for deriving next key
  siteCodeDigitsOnly?: boolean;
  siteCodeRequired?: boolean;
  siteCodeCount?: number;
  minRequiredCodes?: number;
  /** Session 53 — per-code required flags, parallel to the pipe-separated
   *  site-code hashes. PWA renders an "обязателен" marker per input and
   *  gates the submit button. Null → fall back to all-[siteCodeRequired]. */
  siteCodeRequiredFlags?: boolean[];
  mediaUrl?: string;
  mediaType?: string; // IMAGE, VIDEO, AUDIO
  // Additional media attachments beyond the primary (mediaUrl/mediaType).
  // Rendered in order after the primary on the location screen.
  additionalMedia?: Array<{ url: string; type: string }>;
  encryptedHints?: EncryptedHint[]; // multiple hints with individual delays
}

/** Session 53 — one location's plaintext answers for the demo autopilot.
 *  Mirrors backend `DemoStep` (Game.kt). Wire-only — server populates,
 *  PWA bot consumes, no real-player code path ever sees this. */
export interface DemoStep {
  locationId: string;
  answerType: 'COORDINATES' | 'ADDRESS';
  lat?: number;
  lon?: number;
  city?: string;
  street?: string;
  house?: string;
  /** Pipe-separated, e.g. "ONE" or "A|B|C". Bot pastes verbatim. */
  siteCode: string;
}

/** Session 70 (#13) — aggregate reaction tally per (message, emoji). `mine`
 *  = viewer reacted with this emoji. Server sends `mine=false` in WS
 *  broadcasts; each PWA patches its own `mine` from `actorPlayerId` on
 *  REACTION_CHANGED. In history fetches (`getChatMessages`), `mine` is
 *  already viewer-correct (server resolves via the requester's playerId). */
export interface ReactionTally {
  emoji: string;
  count: number;
  mine: boolean;
}

export interface ChatMessage {
  id: string;
  teamName: string;
  senderType: string; // PLAYER, ORGANIZER
  message: string;
  timestamp: number;
  mediaUrl?: string;
  /** Session 70 (#13) — optional because older servers don't populate. */
  reactions?: ReactionTally[];
  /** Session 87 — frozen display name from session-52 contract (Game.kt:429).
   *  Optional because older builds may not populate. Falls back to senderType
   *  literal at render time. */
  senderName?: string;
  /** Session 87 — channel discriminator from session-52 contract.
   *  LOBBY = pre-start lobby chat, TEAM = team chat (which is also where
   *  org→team pre-start messages land — see Lobby targeted-team feature). */
  channel?: 'LOBBY' | 'TEAM';
}

export interface LeaderboardEntry {
  teamId: string;
  teamName: string;
  locationsCompleted: number;
  totalLocations: number;
  totalTimeMs: number; // already includes penaltyMs (base + penalty)
  penaltyMs: number;
  wrongAnswers: number;
  finished: boolean;
}

export interface GameState {
  // Connection
  gameCode: string;
  gameTitle: string;
  playerId: string;
  playerName: string;
  playerRole: PlayerRole;
  isCaptain: boolean;
  teamName: string;
  organizerPhone: string;
  fullOffline: boolean;
  allowManualSkip: boolean;
  token: string;
  playerToken: string; // per-player auth token, sent as X-Player-Token

  // Game
  status: GameStatus;
  players: Player[];
  locations: Location[];
  currentLocationIndex: number;
  locationStartedAt: number; // timestamp ms
  gameStartedAt: number; // timestamp ms — set on join OR on GAME_STARTED WS handler
  salt: string;
  firstRiddleKey: string;

  // Keys for progressive decryption chain (base64 from server)
  nextKeys: string[];

  // Decrypted riddle/hint for current location (set by location-view for crash recovery)
  currentRiddleText: string;
  currentHintText: string;

  // Chat
  chatMessages: ChatMessage[];
  unreadChatCount: number;
  allowTeamChat: boolean;
  canChat: boolean; // server-computed: this player is allowed to send chat messages

  // Results
  completedAt: number;
  totalTimeMs: number;
  wrongAnswers: number;
  penaltyMs: number; // accumulated manual-skip penalties (ms)

  // Leaderboard
  leaderboard: LeaderboardEntry[];

  // Lobby / demo settings
  lobbyChatMode: string;
  gameRules: string | null;
  demoSpeedMultiplier: number;
  /** Session 53 — plaintext per-location answers for the demo autopilot.
   *  Set by createDemoSession via JoinGameResponse.demoScript. When non-
   *  empty, location-view runs a bot that fills inputs after a short
   *  delay per phase. NEVER populated for real player joins. */
  demoScript: DemoStep[];

  // Hint auto-navigate data (consumed by location-view to restore site-code phase on remount)
  hintNavigateData?: { lat: number; lon: number; city?: string; street?: string; house?: string };

  // Verified geo answer — persisted so returning from chat restores site-code phase.
  // /investigate 2026-04-27 follow-up — `lat`/`lon` can be `null` for ADDRESS-type
  // locations whose target address has no geocoded coords (org just typed strings,
  // never resolved to a FIAS pin). Server WS broadcast and /player-state both
  // forward `null` in that case; address alone carries the verification signal.
  geoVerified?: { lat: number | null; lon: number | null; address?: string };

  // UI state
  loading: boolean;
  error: string | null;
  wsConnected: boolean;
}

const initialState: GameState = {
  gameCode: '',
  gameTitle: '',
  playerId: '',
  playerName: '',
  playerRole: 'PLAYER',
  isCaptain: false,
  teamName: '',
  organizerPhone: '',
  fullOffline: false,
  allowManualSkip: false,
  token: '',
  playerToken: '',
  status: 'WAITING',
  players: [],
  locations: [],
  currentLocationIndex: 0,
  locationStartedAt: 0,
  gameStartedAt: 0,
  salt: '',
  firstRiddleKey: '',
  nextKeys: [],
  currentRiddleText: '',
  currentHintText: '',
  chatMessages: [],
  unreadChatCount: 0,
  allowTeamChat: false,
  canChat: false,
  completedAt: 0,
  totalTimeMs: 0,
  wrongAnswers: 0,
  penaltyMs: 0,
  leaderboard: [],
  lobbyChatMode: 'OFF',
  gameRules: null,
  demoSpeedMultiplier: 1,
  demoScript: [],
  loading: false,
  error: null,
  wsConnected: false,
};

type Listener = (state: GameState) => void;

let state: GameState = { ...initialState };
const listeners = new Set<Listener>();

export function getState(): GameState {
  return state;
}

export function setState(partial: Partial<GameState>): void {
  state = { ...state, ...partial };
  persistState(state);
  listeners.forEach(fn => fn(state));
}

export function resetState(): void {
  state = { ...initialState };
  clearPersistedState();
  listeners.forEach(fn => fn(state));
}

/** Restore state from localStorage if available (survives crashes) */
export function restorePersistedState(): boolean {
  const persisted = loadPersistedState();
  if (persisted) {
    state = { ...state, ...persisted };
    return true;
  }
  return false;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
